/**
 * src/lib/voice/uploadStatusStore.js — TICKET-UPLOAD-PROGRESS-UX (CEO 2026-06-02).
 *
 * A tiny module-singleton store of DIRECT-upload status "jobs", rendered by
 * VoiceQueueTray alongside its IDB-queue sessions so there is ONE status surface
 * for all voice intake (CEO decision: top strip · all voice intake · stays until
 * dismissed · minimal + History link).
 *
 * Direct file uploads (uploadFileWithBreadcrumb) bypass the IndexedDB queue — iOS
 * Safari chokes on large IDB blobs — so they're invisible to useVoiceQueueState
 * (which reads getAllRecordings). This store gives them a home WITHOUT putting any
 * bytes in IDB: just status metadata. The queue worker never sees it (it only reads
 * IDB), so there's no spurious re-processing.
 *
 * Job shape: { id, fileName, phase, pct, uploadId, headline, error, createdAt }
 *   phase: 'uploading' | 'uploaded' | 'processing' | 'classifying' | 'ready' | 'error'
 *
 * Stays until dismissed: removeUploadJob is called only by the user's dismiss tap
 * (no auto-fade), per the CEO decision.
 */

let jobs = [];
const listeners = new Set();

function emit() {
  jobs = jobs.slice(); // new ref so useSyncExternalStore re-renders
  listeners.forEach((l) => { try { l(); } catch { /* spine-emit-ok: listener errors must not break the store */ } });
}

/** Stable snapshot for useSyncExternalStore (same ref unless changed). */
export function getUploadJobs() { return jobs; }

export function subscribeUploadJobs(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Insert or merge a job by id. */
export function upsertUploadJob(job) {
  if (!job || !job.id) throw new Error('upsertUploadJob: id is required');
  const i = jobs.findIndex((j) => j.id === job.id);
  if (i >= 0) {
    jobs[i] = { ...jobs[i], ...job };
  } else {
    jobs.push({ phase: 'uploading', pct: 0, createdAt: Date.now(), ...job });
  }
  emit();
}

/** Merge a patch into an existing job. No-op if absent. */
export function patchUploadJob(id, patch) {
  const i = jobs.findIndex((j) => j.id === id);
  if (i < 0) return;
  jobs[i] = { ...jobs[i], ...patch };
  emit();
}

/** Remove a job (user dismiss). */
export function removeUploadJob(id) {
  const before = jobs.length;
  jobs = jobs.filter((j) => j.id !== id);
  if (jobs.length !== before) emit();
}

/**
 * Map a voice_uploads.status to a stepper phase (the History-tab "Processing
 * recording" toast). Returns null for an unknown/transient status so the caller
 * leaves the current phase untouched rather than flickering. The History tab
 * reconciles active jobs from its polled+realtime `uploads` rows through this.
 */
export function uploadStatusToPhase(status) {
  switch (status) {
    case 'uploading':
    case 'queued': return 'uploading';
    case 'uploaded': return 'uploaded';
    case 'processing': return 'processing';
    case 'classifying': return 'classifying';
    // TICKET-1127: 'classified' is where every finished upload actually ends.
    // Unmapped, it returned null and the stepper froze on "Updating project".
    case 'classified':
    case 'ready': return 'ready';
    case 'error':
    case 'failed': return 'error';
    default: return null;
  }
}

/** Test helper. */
export function _resetUploadJobs() { jobs = []; listeners.clear(); }
